import { ErrorModal, HelmetDexteel, MesfModal } from "@dexteel/mesf-core";
import { Grid2, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	CATALOG_PAINT_CUT_TYPE_INITIAL_VALUES,
	CatalogPaintCutType,
} from "@/shared/models/CatalogPaintCutType";
import {
	deleteCatalogPaintCutType,
	upsertCatalogPaintCutType,
} from "@/shared/repositories/CatalogPaintCutTypeRepository";

type Props = {
	paintCutType: CatalogPaintCutType | null;
	modal: "create" | "update" | "delete" | "";
	onHide: (shouldUpdate?: boolean) => void;
};

export const UpsertDeleteModal = ({ paintCutType, modal, onHide }: Props) => {
	const [title, setTitle] = useState<string>("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [error, setError] = useState("");

	const successMessage = `The paint cut type was ${modal}d successfully`;

	const { control, reset, handleSubmit } = useForm<CatalogPaintCutType>({});

	const queryClient = useQueryClient();

	const deleteMutation = useMutation({
		mutationFn: (paintCutTypeId: number) =>
			deleteCatalogPaintCutType(paintCutTypeId),
		onSuccess: async (response) => {
			if (response.ok) {
				await queryClient.invalidateQueries({
					queryKey: ["catalogPaintCutTypes"],
				});
				setNotificationMessage(successMessage);
				onHide(true);
			} else {
				setError(response.message);
			}
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const upsertMutation = useMutation({
		mutationFn: (data: CatalogPaintCutType) => upsertCatalogPaintCutType(data),
		onSuccess: async (response) => {
			if (response.ok) {
				await queryClient.invalidateQueries({
					queryKey: ["catalogPaintCutTypes"],
				});
				setNotificationMessage(successMessage);
				onHide(true);
			} else {
				setError(response.message);
			}
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const onSubmit: SubmitHandler<CatalogPaintCutType> = async (
		data: CatalogPaintCutType,
	) => {
		if (modal === "delete" && data.PaintCutTypeId) {
			deleteMutation.mutate(data.PaintCutTypeId);
		} else {
			upsertMutation.mutate(data);
		}
	};

	useEffect(() => {
		if (modal !== "") {
			reset(
				modal === "create"
					? CATALOG_PAINT_CUT_TYPE_INITIAL_VALUES
					: paintCutType || CATALOG_PAINT_CUT_TYPE_INITIAL_VALUES,
			);
			setTitle(
				modal === "create" ? "CREATE" : modal === "update" ? "EDIT" : "DELETE",
			);
		}
	}, [modal, paintCutType, reset]);

	return (
		<>
			{(modal === "create" || modal === "update" || modal === "delete") && (
				<HelmetDexteel
					title={`${
						modal === "create"
							? "New"
							: `${modal === "update" ? "Edit" : "Delete"}`
					} Paint Cut Type - Paint Cut Types`}
				/>
			)}
			<MesfModal
				id="modal-paint-cut-type"
				title={`${title} PAINT CUT TYPE`}
				open={modal !== ""}
				handleClose={() => onHide(false)}
				maxWidth="sm"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content style={{ padding: "15px 30px" }}>
						<Grid2 container spacing={2}>
							<Grid2 size={{ md: 12, xs: 12 }}>
								<Controller
									name="PaintCutTypeName"
									control={control}
									rules={{
										required: "Name is required",
									}}
									render={({ field, fieldState: { error } }) => (
										<TextField
											label="Name"
											variant="outlined"
											value={field.value || ""}
											onChange={field.onChange}
											error={!!error}
											helperText={error?.message}
											fullWidth
											autoComplete="off"
											size="small"
											disabled={modal === "delete"}
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<FooterModalButtons
							isSubmitLoading={
								deleteMutation.isPending || upsertMutation.isPending
							}
							onHide={onHide}
							buttonLabel={modal === "delete" ? "Delete" : "Save"}
							buttonColor={modal === "delete" ? "secondary" : "primary"}
						/>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
